import { v4 as uuid } from 'uuid';
import {
  ACTIVITY_TYPES,
  CUSTOM_ROUTINE_OPTION_PREFIX,
  EMPTY_STRING
} from 'constants/general';
import { ActivityType } from 'interfaces/common.interface';
import { RoutineDestination, SettingsType } from 'interfaces/setting.interface';
import { getRoutineActivitiesOfCurrentSettings } from 'utils/helpers';

export const toCustomRoutineOptionValue = (routineId: string): string =>
  `${CUSTOM_ROUTINE_OPTION_PREFIX}${routineId}`;

export const isCustomRoutineOptionValue = (optionValue: string): boolean =>
  optionValue.startsWith(CUSTOM_ROUTINE_OPTION_PREFIX);

export const getCustomRoutineIdFromOptionValue = (
  optionValue: string
): string => optionValue.slice(CUSTOM_ROUTINE_OPTION_PREFIX.length);

const getBuiltInRoutineSequenceId = (
  routineType: string,
  currentSettings: SettingsType
): string =>
  getRoutineActivitiesOfCurrentSettings(routineType, currentSettings)?.[0]
    ?.activity_sequence_id ?? EMPTY_STRING;

const resolveCustomRoutineDestination = (
  optionValue: string,
  currentSettings: SettingsType
): RoutineDestination | undefined => {
  const routineId = getCustomRoutineIdFromOptionValue(optionValue);
  const customRoutines = currentSettings.custom_routines ?? [];
  const routineIndex = customRoutines.findIndex(
    (routine) => routine.id === routineId
  );
  if (routineIndex < 0) {
    return undefined;
  }
  return {
    destination_type: ACTIVITY_TYPES.CUSTOM_ROUTINES,
    destination_sequence_id:
      customRoutines[routineIndex].activity_sequence_id ?? EMPTY_STRING,
    destination_customRoutinePosition: routineIndex
  };
};

// Maps a move/copy dropdown value (built-in routine key or prefixed custom routine id) to where the habit should land.
export const resolveRoutineDestination = (
  optionValue: string,
  currentSettings: SettingsType
): RoutineDestination | undefined => {
  if (isCustomRoutineOptionValue(optionValue)) {
    return resolveCustomRoutineDestination(optionValue, currentSettings);
  }
  return {
    destination_type: optionValue,
    destination_sequence_id: getBuiltInRoutineSequenceId(
      optionValue,
      currentSettings
    )
  };
};

// Shared by Move Habit and Make a Copy: a habit entering another routine joins that routine's sequence and drops its choice configuration.
export const toRoutineDestinationActivity = (
  activity: ActivityType,
  destinationSequenceId: string
): ActivityType => ({
  ...activity,
  activity_sequence_id: destinationSequenceId,
  choice_type: undefined,
  choices: []
});

export const appendActivityCopyToRoutine = ({
  routineActivities,
  activity,
  destinationSequenceId
}: {
  routineActivities: ActivityType[];
  activity: ActivityType;
  destinationSequenceId: string;
}): ActivityType[] => [
  ...routineActivities,
  {
    ...toRoutineDestinationActivity(activity, destinationSequenceId),
    id: uuid()
  }
];
